import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { PageWelcome } from '../components/PageWelcome.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { FormIcon } from '../forms/FormIcon.jsx';
import { formatDay } from '../forms/formSchema.js';
import '../forms/forms.css';

const STATE = {
  todo: { label: 'To do', tone: 'amber' },
  submitted: { label: 'Submitted', tone: 'green' },
  closed: { label: 'Closed', tone: 'gray' },
};

export function MyFormsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    apiJson('/api/my/forms')
      .then((data) => setRows(data.data || []))
      .catch((e) => setError(e.message));
    apiJson('/api/my/tasks')
      .then((data) => setRequests(Array.isArray(data) ? data : []))
      .catch(() => setRequests([]));
  }, []);

  const todo = (rows || []).filter((r) => r.state === 'todo');
  const done = (rows || []).filter((r) => r.state !== 'todo');
  const openRequests = requests.filter((task) => task.status !== 'done' && task.status !== 'cancelled');
  const doneRequests = requests.filter((task) => task.status === 'done');

  const requestCard = (task, finished) => (
    <Link key={`task-${task.id}`} to={`/my/tasks?task=${task.id}`} className="fm-card fm-portal-card">
      <span className="fm-row-icon fm-row-icon--client_request">
        <FormIcon name={finished ? 'check' : 'forms'} size={18} />
      </span>
      <span className="fm-portal-card-text">
        <strong>{task.title}</strong>
        {task.description ? <small>{task.description}</small> : null}
        <span className="fm-portal-card-meta">
          {task.project?.name ? <span>{task.project.name}</span> : null}
          <span>Client request</span>
        </span>
      </span>
      <span className={`fm-status fm-status--${finished ? 'green' : 'amber'}`}>
        <span className="fm-status-dot" />
        {finished ? 'Done' : 'To do'}
      </span>
      <span className="fm-portal-card-cta">{finished ? 'View' : 'Open'}</span>
    </Link>
  );

  const card = (row) => {
    const state = row.is_past_due ? { label: 'Past due', tone: 'red' } : STATE[row.state];
    return (
      <Link key={row.id} to={`/my/forms/${row.id}`} className="fm-card fm-portal-card">
        <span className={`fm-row-icon fm-row-icon--${row.state === 'submitted' ? 'website_form' : 'client_request'}`}>
          <FormIcon name={row.state === 'submitted' ? 'check' : 'forms'} size={18} />
        </span>
        <span className="fm-portal-card-text">
          <strong>{row.title}</strong>
          {row.description ? <small>{row.description}</small> : null}
          <span className="fm-portal-card-meta">
            {row.project_name ? <span>{row.project_name}</span> : null}
            {row.due_date ? <span className={row.is_past_due ? 'is-late' : ''}>Due {formatDay(row.due_date)}</span> : null}
            {row.submitted_at ? <span>Submitted {formatDay(row.submitted_at)}</span> : null}
            <span>
              {row.field_count} question{row.field_count === 1 ? '' : 's'}
            </span>
          </span>
        </span>
        <span className={`fm-status fm-status--${state.tone}`}>
          <span className="fm-status-dot" />
          {state.label}
        </span>
        <span className="fm-portal-card-cta">{row.state === 'todo' ? 'Start' : 'View'}</span>
      </Link>
    );
  };

  return (
    <div className="fm-portal">
      <PageWelcome
        title="We need from you"
        subtitle="Forms, questionnaires, and requests your team has asked you to complete."
        actions={
          user?.capabilities?.view_client_faq ? (
            <Link to="/my/faq" className="btn-secondary">
              FAQ & resources
            </Link>
          ) : null
        }
      />
      {error ? <p className="fm-alert fm-alert--error">{error}</p> : null}
      {!rows && !error ? <p className="fm-empty">Loading…</p> : null}
      {rows ? (
        <>
          <section className="fm-portal-section">
            <h2>
              To complete <span className="fm-chip-count">{todo.length + openRequests.length}</span>
            </h2>
            {todo.length || openRequests.length ? (
              <div className="fm-portal-list">
                {openRequests.map((task) => requestCard(task, false))}
                {todo.map(card)}
              </div>
            ) : (
              <div className="fm-card fm-empty">
                <FormIcon name="check" size={26} />
                <p>You are all caught up — nothing is waiting on you.</p>
              </div>
            )}
          </section>
          {done.length || doneRequests.length ? (
            <section className="fm-portal-section">
              <h2>Completed</h2>
              <div className="fm-portal-list">
                {doneRequests.map((task) => requestCard(task, true))}
                {done.map(card)}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
